import { FC } from 'react'
import { ReactComponent as LightbulbIcon } from 'src/assets/svg/lightbulb-line-24.svg'
import { TruncatedTooltip } from 'src/components/TruncatedTooltip'
import styles from './contentFormatComparison.module.scss'
import { ContentCampaign, FormatSplit } from '../interfaces'

interface ContentFormatComparisonProps {
  campaigns: ContentCampaign[]
  insight: string
  rows: FormatSplit[]
}

const COPY = {
  insightTitle: 'Key Insight',
  static: 'Static',
  subtitle: 'Distribution of posts by media type: Video vs Static',
  title: 'Content Format Comparison',
  video: 'Video',
}

// A segment narrower than this cannot hold "Static: 180 (60%)" without the
// label spilling over the join, so it carries no label at all.
const LABEL_FITS = 18

/*
 * Content Format Comparison: one bar per campaign, split video against static.
 *
 * A single 100% bar rather than two measured against a shared axis — the
 * question the widget answers is the mix inside a campaign, not whether one
 * campaign publishes more than another, which the widget above it already
 * answers.
 */
export const ContentFormatComparison: FC<ContentFormatComparisonProps> = ({
  campaigns,
  insight,
  rows,
}) => (
  <section className={styles.card}>
    <div className={styles.headerText}>
      <h3 className={styles.title}>{COPY.title}</h3>
      <p className={styles.subtitle}>{COPY.subtitle}</p>
    </div>

    <div className={styles.rows}>
      {campaigns.map((campaign) => {
        const row = rows.find((item) => item.campaignId === campaign.id)

        if (!row) return null

        return (
          <div className={styles.row} key={campaign.id}>
            <TruncatedTooltip
              className={styles.name}
              maxLines={2}
              text={campaign.name}
            />

            <div className={styles.bar}>
              <div
                className={styles.video}
                style={{ width: `${row.videoPct}%` }}
              >
                {row.videoPct >= LABEL_FITS && (
                  <span className={styles.segmentLabel}>
                    {`${COPY.video}: ${row.videoPosts} (${row.videoPct}%)`}
                  </span>
                )}
              </div>

              <div
                className={styles.staticFormat}
                style={{ width: `${row.staticPct}%` }}
              >
                {row.staticPct >= LABEL_FITS && (
                  <span className={styles.segmentLabel}>
                    {`${COPY.static}: ${row.staticPosts} (${row.staticPct}%)`}
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>

    <ul className={styles.legend}>
      <li className={styles.legendItem}>
        <span className={styles.legendVideo} />
        {COPY.video}
      </li>
      <li className={styles.legendItem}>
        <span className={styles.legendStatic} />
        {COPY.static}
      </li>
    </ul>

    {/* The server sends an empty string when the window holds nothing worth a
        sentence; a heading and an icon around empty space is worse than no
        box at all. */}
    {insight && (
      <div className={styles.insight}>
        <LightbulbIcon className={styles.insightIcon} />

        <div className={styles.insightText}>
          <p className={styles.insightTitle}>{COPY.insightTitle}</p>
          <p className={styles.insightBody}>{insight}</p>
        </div>
      </div>
    )}
  </section>
)
